import React, {useCallback, useEffect, useId} from 'react';
import {useTranslation} from 'react-i18next';
import {Button, CurvedArrowLeftIcon, CurvedArrowRightIcon} from '@taopix/taopix-design-system';
import {useTaopix} from '../Context/TaopixContext';
import {useUiState} from '../Context/UserInterface/UserInterfaceContext';
import {CoreEvent} from '@taopix/taopix-core';
import {useExperience} from '../Experience/ExperienceProvider';
import {useSelector} from 'react-redux';
import {TotalPageCountSelector} from '../../Selectors';
import {
  ExperienceType,
  ProductLayout,
  UndoRedoResponse,
} from '@taopix/taopix-core/dist/types/types';
import {onPageCountChanged} from '../../Utils/Page';
import {UserInterfaceActions} from '../Context/UserInterface/UserInterfaceActionTypes';
import {ButtonProps} from '@taopix/taopix-design-system/dist/types/Components/Button/Button';

export type HistoryState = {
  undo: boolean;
  redo: boolean;
};

const UndoRedoButtons = ({buttonProps}: {buttonProps?: Partial<ButtonProps>}) => {
  const app = useTaopix();
  const ui = useUiState();
  const {t} = useTranslation();
  const {updateExperience} = useExperience();
  const pageCount = useSelector(TotalPageCountSelector);
  const history = app.core.get('history');

  useEffect(() => {
    const tokens = [
      app.core.subscribe(CoreEvent.HISTORY_ADD, handleHistoryState),
      // Catch undo/redo from keyboard shortcuts.
      app.core.subscribe(
        CoreEvent.UNDO_REDO_KEYBOARD,
        (msg: CoreEvent, response: UndoRedoResponse) => {
          onUndoRedo(response);
        }
      ),
    ];

    // call the handler in case any history actions have been applied in the full screen assistant
    handleHistoryState();

    return () => {
      tokens.forEach(token => app.core.unsubscribe(token));
    };
  }, []);

  /**
   * History state handler
   */
  const handleHistoryState = () => {
    ui.dispatch(
      UserInterfaceActions.setHistoryState({
        undo: history.canUndo(),
        redo: history.canRedo(),
      })
    );
  };

  /**
   * Trigger an undo/redo
   *
   * @param type If it's an undo or redo action.
   */
  const doHistory = (type: 'undo' | 'redo'): void => {
    if (ui.state.history.hasOwnProperty(type)) {
      history[type]().then(response => {
        onUndoRedo(response);
      });
    }
  };

  /**
   * Common actions to execute after an undo/redo..
   *
   * @param response The response object from undo/redo.
   */
  const onUndoRedo = useCallback((response: UndoRedoResponse) => {
    handleHistoryState();

    updateExperience(app.core.get('experience') as unknown as ExperienceType);

    if (response.spineResized) {
      onPageCountChanged(
        {spineResized: response.spineResized},
        ui,
        response?.project.productLayout as ProductLayout,
        pageCount
      );
    }
  }, []);

  const id = useId().replace(new RegExp(':', 'g'), '');

  return (
    <>
      <Button
        id={'tpx-btnUndo-' + id}
        label={t('str_ButtonUndo')}
        hideLabel={ui.state.isSmallScreen}
        startIcon={<CurvedArrowLeftIcon id={'tpx-svg-btnUndo'} />}
        tabPosition={'left'}
        disabled={!ui.state.history.undo}
        onClick={e => doHistory('undo')}
        className={'rounded-l-themeCornerSize'}
        corners={'square'}
        buttonStyle={'standard'}
        size={'small'}
        {...buttonProps}
      />
      <Button
        id={'tpx-btnRedo-' + id}
        label={t('str_ButtonRedo')}
        hideLabel={ui.state.isSmallScreen}
        startIcon={<CurvedArrowRightIcon id={'tpx-svg-btnRedo'} />}
        tabPosition={'right'}
        disabled={!ui.state.history.redo}
        onClick={e => doHistory('redo')}
        className={'rounded-r-themeCornerSize'}
        corners={'square'}
        buttonStyle={'standard'}
        {...buttonProps}
      />
    </>
  );
};

export default UndoRedoButtons;
